import { DataSources } from '~src/datasource/resources/Datasources';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { ISelectionQuery } from '../serverApi/types/ISelectionQuery';

/** Operator enum NAME the server expects for the single-id selection shortcut. */
const EQUALS_OPERATOR = 'EQUALS';

/**
 * Explore content types the client knows how to add to a relay plan, mapped to the server's
 * selection-query `content_type` strings (the tabhf-content-mobility `ContentType` enum names).
 *
 * This is the client's vocabulary, not the supported set: which of these are offered right now is
 * decided by the server's `listSupportedContentTypes` response (see supportedContentTypes.ts). A type
 * the server releases also needs an entry here (and in the Add to Relay Plan entry of ActionsList), plus a
 * localized label in planContentTypeLabel.
 */
const CONTENT_TYPE_TO_WIRE: { [key: string]: string } = {
  workbook: 'WORKBOOKS',
  datasource: 'DATASOURCES',
  project: 'PROJECTS',
};

/** The server `content_type` string for an Explore content type, or undefined if not relayable. */
export function relayWireContentType(contentType: ExplorableItemType): string {
  return CONTENT_TYPE_TO_WIRE[contentType];
}

/**
 * The content-core DB id the selection stores for an item. Data sources listed from the unified data
 * source core (getDatasources) carry a unified id in `id` — really a data connection id — and the data
 * source's own id in `idForFavoriting`; exploreContent items already carry the DB id.
 */
export function relayItemId(item: IContentInfo): string {
  if (DataSources.isUnifiedDatasource(item)) {
    return item.idForFavoriting ? String(item.idForFavoriting) : undefined;
  }
  return item.id ? String(item.id) : undefined;
}

/** True when the item is a supported content type and carries the numeric id the selection needs. */
export function isRelayableItem(item: IContentInfo): boolean {
  return !!relayWireContentType(item.contentType) && !!relayItemId(item);
}

/** Operator enum NAME for an explicit id list; the value is a JSON array of ids. */
const IN_OPERATOR = 'IN';

/**
 * Elements of an IN criterion's JSON-array value; undefined if it isn't a non-empty array of scalars
 * (what tabhf-content-mobility WhereConditionBuilder.parseInValues accepts).
 */
export function parseInValues(value: string): Array<string | number> {
  let parsed: any;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    return undefined;
  }
  if (!Array.isArray(parsed) || !parsed.length) {
    return undefined;
  }
  const scalars = parsed.every(function(el: any) { return typeof el === 'string' || typeof el === 'number'; });
  return scalars ? parsed : undefined;
}

/**
 * The ids an explicit-id selection query lists: a lone `id EQUALS <id>` or a lone `id IN [..]`.
 * Undefined for any other query shape — those select by filter and only the server can resolve them.
 */
export function explicitSelectionIds(query: ISelectionQuery): string[] {
  if (!query || !query.criteria || query.criteria.length !== 1) {
    return undefined;
  }
  const criterion = query.criteria[0];
  if (criterion.path !== 'id' || !criterion.value) {
    return undefined;
  }
  if (criterion.operator === EQUALS_OPERATOR) {
    return [String(criterion.value)];
  }
  if (criterion.operator === IN_OPERATOR) {
    const values = parseInValues(criterion.value);
    return values ? values.map(String) : undefined;
  }
  return undefined;
}

/**
 * The selection query for a content type's explicit ids: the single-id shortcut (`id EQUALS <dbId>`)
 * for one id, which the server resolves including related content, else `id IN [..]`. The service
 * allows at most one selection query per content type (DefaultPlanValidator), so all of a type's ids
 * go in one query.
 */
function idQuery(contentType: string, ids: ReadonlyArray<string>): ISelectionQuery {
  return {
    contentType: contentType,
    criteria: ids.length === 1
      ? [{ path: 'id', operator: EQUALS_OPERATOR, value: ids[0] }]
      : [{ path: 'id', operator: IN_OPERATOR, value: JSON.stringify(ids) }],
  };
}

/** Adds `ids` to `target` (skipping ones already there); returns how many were new. */
function addIds(target: string[], ids: ReadonlyArray<string>): number {
  let added = 0;
  ids.forEach(function(id) {
    if (target.indexOf(id) === -1) {
      target.push(id);
      added++;
    }
  });
  return added;
}

/**
 * Maps the selected Explore items to selection queries, dropping non-relayable items: one explicit-id
 * query per content type (see idQuery). NOTE: the ids are numeric DB ids (see relayItemId), not LUIDs.
 */
export function buildSelectionQueriesForItems(items: ReadonlyArray<IContentInfo>): ISelectionQuery[] {
  return mergeSelectionQueries([], items.filter(isRelayableItem).map(function(item) {
    return idQuery(relayWireContentType(item.contentType), [relayItemId(item)]);
  })).merged;
}

/**
 * Builds the full selection set for an "add to existing plan" update. Since `updatePlan` replaces
 * `selectionQueries` wholesale, every existing query is kept. Incoming ids are merged into the plan's
 * explicit-id query for that content type (one query per type, at the position of the type's first
 * one), skipping ids already present. Filter queries are kept or appended as-is. `addedCount` is how
 * many ids (or filter queries) were actually new, for the success toast.
 */
export function mergeSelectionQueries(
  existing: ReadonlyArray<ISelectionQuery>,
  incoming: ReadonlyArray<ISelectionQuery>
): { merged: ISelectionQuery[], addedCount: number } {
  // Ids per content type, plus a slot in `merged` filled with the type's query once all ids are in.
  const idsByType: { [contentType: string]: { first: ISelectionQuery, ids: string[], changed: boolean } } = {};
  const slots: Array<ISelectionQuery | string> = [];
  const place = function(query: ISelectionQuery): number {
    const ids = explicitSelectionIds(query);
    if (!ids) {
      slots.push(query);
      return 1;
    }
    const entry = idsByType[query.contentType];
    if (!entry) {
      idsByType[query.contentType] = { first: query, ids: ids.slice(), changed: false };
      slots.push(query.contentType);
      return ids.length;
    }
    const added = addIds(entry.ids, ids);
    entry.changed = true;
    return added;
  };

  (existing || []).forEach(place);
  let addedCount = 0;
  incoming.forEach(function(query) { addedCount += place(query); });

  // A type listed by a single query keeps that query object untouched.
  const merged = slots.map(function(slot) {
    if (typeof slot !== 'string') {
      return slot;
    }
    const entry = idsByType[slot];
    return entry.changed ? idQuery(slot, entry.ids) : entry.first;
  });
  return { merged: merged, addedCount: addedCount };
}
